import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        // Don't retry on 4xx - these are client errors that won't resolve themselves.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

export const qk = {
  me: ["me"] as const,
  curriculum: {
    classes: () => ["curriculum", "classes"] as const,
    subjects: (classId: string) => ["curriculum", classId, "subjects"] as const,
    chapters: (subjectId: string) => ["curriculum", "subject", subjectId, "chapters"] as const,
  },
  content: {
    sign: (chapterId: string, assetType: string) =>
      ["content", chapterId, assetType] as const,
  },
  progress: {
    dashboard: () => ["progress", "dashboard"] as const,
    resume: () => ["progress", "resume"] as const,
    streak: () => ["progress", "streak"] as const,
    chapter: (chapterId: string) => ["progress", "chapter", chapterId] as const,
  },
  chat: {
    history: (chapterId: string) => ["chat", chapterId, "history"] as const,
    usage: () => ["chat", "usage"] as const,
  },
  subscriptions: {
    mine: () => ["subscriptions", "me"] as const,
  },
  payments: {
    mine: () => ["payments", "me"] as const,
  },
  admin: {
    users: () => ["admin", "users"] as const,
    user: (id: string) => ["admin", "user", id] as const,
    audit: () => ["admin", "audit"] as const,
    dashboard: () => ["admin", "dashboard"] as const,
    config: (key: string) => ["admin", "config", key] as const,
  },
  // Parent persona (GET /parent/*)
  parent: {
    children: () => ["parent", "children"] as const,
    dashboard: (studentId: string) => ["parent", "child", studentId, "dashboard"] as const,
    heatmap: (studentId: string, days: number) => ["parent", "child", studentId, "heatmap", days] as const,
    subject: (studentId: string, subjectId: string) =>
      ["parent", "child", studentId, "subject", subjectId] as const,
    weakTopics: (studentId: string) => ["parent", "child", studentId, "weak-topics"] as const,
    activity: (studentId: string, days: number) => ["parent", "child", studentId, "activity", days] as const,
    readiness: (studentId: string) => ["parent", "child", studentId, "readiness"] as const,
    alerts: (limit: number) => ["parent", "alerts", limit] as const,
    goals: (studentId: string) => ["parent", "child", studentId, "goals"] as const,
    doubtPatterns: (studentId: string) => ["parent", "child", studentId, "doubt-patterns"] as const,
    assistantHistory: (studentId: string) => ["parent", "assistant", studentId, "history"] as const,
    aiUsage: (studentId: string) => ["parent", "assistant", studentId, "usage"] as const,
    boardExam: (studentId: string) => ["parent", "child", studentId, "board-exam"] as const,
  },
  // Student-side parent-linking surfaces (GET /students/me/parent*)
  studentParent: {
    invites: () => ["student", "parent-invites"] as const,
    parents: () => ["student", "parents"] as const,
  },
  // Student-side goals + notices (Phase 5)
  student: {
    goals: () => ["student", "goals"] as const,
    notices: () => ["student", "notices"] as const,
  },
  // AI test engine (Phase 3)
  tests: {
    mine: () => ["tests", "mine"] as const,
    detail: (testId: string) => ["tests", testId] as const,
  },
  assignments: {
    mine: () => ["assignments", "mine"] as const,
  },
};
